import React, { useMemo } from 'react';

import Link from 'components/link';
import { MenuObjectTypes } from 'enums/menu';
import { getStaticPathname } from 'navigation/location/location';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'pages/menu/hooks/use-get-variant-content-options';
import { logButtonClick } from 'state/amplitude';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { isExternalLink } from 'utils/is-external-link';
import { optionToUrl } from 'utils/menu';
import { startTrace } from 'utils/performance/trace';
import { TraceName } from 'utils/performance/types';

import Tile from '../tile-badges-experiment';
import { TileLinkExternal } from '../tile-link';

import { getCaloriesAndPrice } from './helpers';
import { useCalories } from './hooks';
import { IMenuTile } from './types-badges-experiment';

const SECTION_TILE_STRING = 'section';

// OLD (not one menu)
export const MenuTile = React.memo<IMenuTile>(
  ({ item, objectFitContain = false, badge = null }) => {
    const pathname = getStaticPathname();

    const getCalories = useCalories();

    const { calories, price } = useMemo(
      // we always enable the menu service data here because this is the "new" menu-tile
      () => getCaloriesAndPrice(item, getCalories),
      [getCalories, item]
    );

    const getVariantContentOptions = useGetVariantContentOptions();
    const { description, image, name } = getVariantContentOptions(
      item as IMenuObjectWithContentOptions
    );

    const tile = (
      <Tile
        pathname={pathname}
        hideCalories={!!item.hideCalories}
        calories={calories}
        description={description?.localeRaw}
        id={item._id}
        image={image}
        imageDescription={item.imageDescription?.locale}
        name={name?.locale ?? ''}
        objectFitContain={objectFitContain}
        price={price}
        type={item._type}
        accessibilityLabel={name?.locale ?? ''}
        accessibilityRole="header"
        accessibilityLevel={3}
        badge={badge}
      />
    );

    const redirectUrl = item._type === MenuObjectTypes.SECTION && item?.redirectLink?.locale;

    if (redirectUrl && isExternalLink(redirectUrl)) {
      return (
        <TileLinkExternal key={item._id} href={redirectUrl}>
          {tile}
        </TileLinkExternal>
      );
    }

    const sanityId = item._id;
    const linkPath = redirectUrl || optionToUrl(item);

    const logClickEvent = (tileType: string, productId: string, productName: string) => {
      const isSection = tileType === SECTION_TILE_STRING;
      const attributes = {
        component: isSection
          ? ClickEventComponentNames.PRODUCT_SECTION_TILES
          : ClickEventComponentNames.PRODUCTS,
        name: productName,
        componentId: productId,
      };

      logButtonClick({ attributes }, { logDuplicateClickEvent: true });

      startTrace(isSection ? TraceName.MENU_SECTION_LOAD : TraceName.MENU_PDP_LOAD, {
        menuItemId: productId,
      });
    };

    const handleLinkClick = () => {
      logClickEvent(item._type, item._id, item.name?.locale);
    };

    return (
      <Link
        key={sanityId}
        onPress={handleLinkClick}
        linkPath={linkPath}
        dd-action-name={item.name?.locale}
      >
        {tile}
      </Link>
    );
  }
);
